import * as angular from 'angular';
import { ngStorage } from 'ngstorage';
import * as _ from 'underscore';
import { NotificationService } from '../../../../app.common/modules/common/services/notification.service';
import { ClassificationCategoryListItem } from '../../../../app.retailer/modules/core';
import { ClassificationCategoriesService } from '../classification-categories.service';

interface ICategoryListItem extends ClassificationCategoryListItem {
    Parent?: ICategoryListItem;
    Selected?: boolean;
    CategoryMatch?: boolean;
    SubcategoriesMatch?: boolean;
    Subcategories: ICategoryListItem[];
    AdvertPrice?: number;
}

export interface IChoiceCategoriesTreeOptions {
    showPrices: boolean;
    leafOnly: boolean;
    singleSelect: boolean;
    excludedIDs: number[];
    selectedIDs: number[];
    recentlyUsedCategories: number[];
}

class SelectClassificationCategoriesTreeController {
    public static $inject = ['$localStorage', 'classificationCategoriesService', 'notificationService'];

    public modalInstance: ng.ui.bootstrap.IModalServiceInstance = this as any;

    public resolve: { options: IChoiceCategoriesTreeOptions, languageId: number };

    public filteredCount: number = null;
    public selectedCategories: ICategoryListItem[] = [];
    public expandedNodes = [];
    public categories: ICategoryListItem[] = [];
    public waiting = true;
    public maxCategoriesCount = 100;
    public keywords: string = null;
    public recentlyUsedCategories = {
        all: [] as ICategoryListItem[],
        selected: null,
        selectCategory: (category: ICategoryListItem, select: boolean) => {
            const i = this.recentlyUsedCategories;
            if (select) {
                i.selected = _.findWhere(i.all, { ID: category.ID });
            } else {
                i.selected = null;
            }
        }
    };

    public treeOptions = {
        nodeChildren: 'Subcategories',
        dirSelectable: true,
        multiSelection: true,
        isSelectable: (node: ICategoryListItem) => {
            const isSelectableStandart = (this.resolve.options.selectedIDs || []).indexOf(node.ID) === -1;
            if (this.resolve.options.singleSelect && this.resolve.options.leafOnly) {
                return isSelectableStandart && node.Subcategories.length === 0;
            }
            return isSelectableStandart;
        },
        injectClasses: {
            ul: 'a1',
            li: 'a2',
            liSelected: 'liSelected',
            iExpanded: 'a3',
            iCollapsed: 'a4',
            iLeaf: 'a5',
            label: 'a6',
            labelSelected: 'a8'
        },
        equality: (x: any, y: any) => x === y
    };

    public currencySign: string;
    public countryID: number;

    constructor(
        private $localStorage: ngStorage.StorageService,
        private categoriesService: ClassificationCategoriesService,
        private notificationService: NotificationService
    ) { }

    public async $onInit() {
        this.notificationService.showWaitingMessage();

        if (this.resolve.options.singleSelect && this.$localStorage.recentlyUsedClassificationCategories) {
            this.resolve.options.recentlyUsedCategories = this.$localStorage.recentlyUsedClassificationCategories;
        }

        const categories = await this.categoriesService.getTree(this.resolve.languageId);
        this.categories = this.getCategories(categories as ICategoryListItem[], this.resolve.options.excludedIDs || [], this.resolve.options.selectedIDs || []);

        if (this.resolve.options.leafOnly && _.size(this.resolve.options.recentlyUsedCategories) > 0) {
            const flatten = this.categoriesService.flattenCategories(this.categories) as ICategoryListItem[];
            _.forEach(this.resolve.options.recentlyUsedCategories, (id: number) => {
                const category = _.findWhere(flatten, { ID: id });
                if (category) {
                    this.recentlyUsedCategories.all.push(category);
                }
            });
        }
        this.waiting = false;
        this.notificationService.clearAll();
        const promise  = this.categoriesService.getCountry();
        promise.then((result) => {
                this.currencySign = result.CurrencySign;
            });

        this.countryID = this.$localStorage.retailerAuthorizationData.countryID;
    }

    public categoryMatch = (category: ICategoryListItem): boolean => {
        return category.CategoryMatch || category.SubcategoriesMatch;
    }

    public countToAdd() {
        return this.getCategoriesToAdd().length;
    }

    public totalPrice() {
        return _.reduce(_.pluck(this.getCategoriesToAdd(), 'AdvertPrice'), function(x: number, y: number) {
            return x + y;
        }, 0);
    }

    public filterCategories() {
        const keywords = this.keywords;
        this.setCategoriesKeywordsMatch(keywords).then(() => {
            if (this.resolve.options.leafOnly) {
                const selectedLeafCategories = this.selectedCategories.filter((category) => {
                    return category.Subcategories.length === 0;
                });
                this.selectedCategories.forEach(this.resetSelected);
                this.selectedCategories = this.getSelectedCategories(selectedLeafCategories);
                this.selectedCategories.forEach(this.setSelected);
            }

            this.expandedNodes = [];
            if (!keywords) {
                this.selectedCategories.forEach((category) => {
                    let parent = category.Parent;
                    let added = false;
                    while (parent) {
                        if (added || _.difference(parent.Subcategories, this.selectedCategories).length > 0) {
                            this.expandedNodes.push(parent);
                            added = true;
                        }

                        parent = parent.Parent;
                    }
                });
                this.expandedNodes = _.uniq(this.expandedNodes);
            }

            const filtered = this.categories.filter(this.categoryMatch);
            let expandedCount = filtered.length;
            this.filteredCount = filtered.length;
            filtered.forEach((category) => {
                expandedCount = this.setExpandedNodes(category, expandedCount);
            });
        });
    }

    public add = () => {
        const categories = this.getCategoriesToAdd();
        if (this.resolve.options.singleSelect) {
            this.setRecentlyUsedClassificationCategories(categories);
        }
        this.modalInstance.close({
            $value: this.resolve.options.singleSelect ? [categories[0]] : categories
        });
    }

    public discard = () => {
        this.modalInstance.dismiss({ $value: 'cancel' });
    }

    public recentlyUsedCategoryClick = () => {
        if (this.recentlyUsedCategories.selected) {
            this.expandedNodes.push(this.recentlyUsedCategories.selected);
            if (this.resolve.options.singleSelect) {
                this.selectedCategories = [this.recentlyUsedCategories.selected];
            }
        }
    }

    public nodeToggle = (node: ICategoryListItem, expanded: boolean, parentNode: ICategoryListItem, index: number) => {
        if (!expanded) {
            this.expandedNodes = _.without(this.expandedNodes, node);
        }
    }

    private getCategories(categories: ICategoryListItem[], excludedIDs: number[], selectedIDs: number[]) {
        const result: ICategoryListItem[] = [];
        categories.forEach((category) => {
            if (excludedIDs.indexOf(category.ID) === -1) {
                const subcategories = this.getCategories(category.Subcategories, excludedIDs, selectedIDs);
                if (subcategories.length > 0 || category.Subcategories.length === 0) {
                    const mapped = {
                        ID: category.ID,
                        ParentID: category.ParentID,
                        Name: category.Name,
                        FullName: category.FullName,
                        AdvertPrice: category.AdvertPrice,
                        Subcategories: subcategories,
                        CategoryMatch: true,
                        SubcategoriesMatch: true
                    };

                    mapped.Subcategories.forEach((subcategory) => {
                        subcategory.Parent = mapped;
                    });

                    result.push(mapped);
                }
            }
        });
        return result;
    }

    private flatten(category: ICategoryListItem) {
        return [category].concat(_.flatten(category.Subcategories.map(this.flatten), true));
    }

    private setSelected(category: ICategoryListItem) {
        category.Selected = true;
    }

    private resetSelected(category: ICategoryListItem) {
        category.Selected = false;
    }

    private setExpandedNodes(category: ICategoryListItem, count: number) {
        const maxExpandedCount = 50;

        if (count < maxExpandedCount && !category.CategoryMatch && category.SubcategoriesMatch) {
            this.expandedNodes.push(category);
            const filtered = category.Subcategories.filter(this.categoryMatch);
            count += filtered.length;
            for (let i = 0; i < filtered.length && count < maxExpandedCount; i++) {
                count = this.setExpandedNodes(filtered[i], count);
            }
        }

        return count;
    }

    private setCategoryMatch(category: ICategoryListItem, matcher: (category: ICategoryListItem) => boolean, parentMatch: boolean) {
        category.CategoryMatch = !matcher || parentMatch || matcher(category);
        category.Subcategories.forEach((subcategory) => {
            this.setCategoryMatch(subcategory, matcher, category.CategoryMatch);
        });
        category.SubcategoriesMatch = !matcher || category.CategoryMatch || _.some(category.Subcategories, this.categoryMatch);
    }

    private setCategoriesMatch(matcher: (category: ICategoryListItem) => boolean) {
        if (_.isArray(this.categories)) {
            this.categories.forEach((category) => {
                this.setCategoryMatch(category, matcher, false);
            });
        }
    }

    private setCategoriesRegexMatch(regex: RegExp) {
        this.setCategoriesMatch((category: ICategoryListItem): boolean => {
            return regex.test(category.Name);
        });
    }

    private setCategoriesIDsMatch(ids: number[]) {
        this.setCategoriesMatch((category: ICategoryListItem): boolean => {
            return ids.indexOf(category.ID) !== -1;
        });
    }

    private setCategoriesAllMatch() {
        this.setCategoriesMatch((category: ICategoryListItem): boolean => {
            return true;
        });
    }

    private async setCategoriesKeywordsMatch(keywords: string) {
        if (!keywords || _.isEmpty(keywords)) {
            this.setCategoriesAllMatch();
            return;
        }

        const ids = await this.categoriesService.categoriesSearch(keywords);
        if (ids.length > 0) {
            this.setCategoriesIDsMatch(ids);
        } else {
            this.setCategoriesRegexMatch(new RegExp('\\b' + keywords + '.*', 'i'));
        }
    }

    private getSelectedCategories(leafCategories: ICategoryListItem[]) {
        const result = leafCategories;
        leafCategories.forEach((category) => {
            let parent = category.Parent;
            while (parent && _.difference(parent.Subcategories.filter(this.categoryMatch), result).length === 0) {
                result.push(parent);
                parent = parent.Parent;
            }
        });
        return _.uniq(result);
    }

    private selectNested(category: ICategoryListItem) {
        const subcategories = this.flatten(category).filter(this.categoryMatch);
        subcategories.forEach(this.setSelected);
        this.selectedCategories = _.union(this.selectedCategories, subcategories);
    }

    private deselectNested(category: ICategoryListItem) {
        const subcategories = this.flatten(category).filter(this.categoryMatch);
        subcategories.forEach(this.resetSelected);
        this.selectedCategories = _.difference(this.selectedCategories, subcategories);
    }

    private selectParent(category: ICategoryListItem) {
        _.all(category.Parent.Subcategories, (category) => category.Selected === true);
        while (category.Parent && _.all(category.Parent.Subcategories, (category) => category.Selected === true)) {
            category = category.Parent;
            this.setSelected(category);
            this.selectedCategories.push(category);
        }
    }

    private deselectParent(category: ICategoryListItem) {
        while (category.Parent && category.Parent.Selected) {
            category = category.Parent;
            this.resetSelected(category);
            this.selectedCategories = _.without(this.selectedCategories, category);
        }
    }

    private getCategoriesToAdd() {
        return this.resolve.options.leafOnly
            ? this.selectedCategories.filter((category) => {
                return category.Subcategories.length === 0;
            })
            : this.selectedCategories;
    }

    private setRecentlyUsedClassificationCategories(categories: ClassificationCategoryListItem[]) {
        const maxCount = 5;
        let recentlyUsedCategories: number[] = this.$localStorage.recentlyUsedClassificationCategories ? this.$localStorage.recentlyUsedClassificationCategories : [];
        _.forEach(categories, (category) => {
            recentlyUsedCategories = _.reject(recentlyUsedCategories, (id) => id === category.ID);
            recentlyUsedCategories.unshift(category.ID);
        });
        this.$localStorage.recentlyUsedClassificationCategories = _.first(recentlyUsedCategories, maxCount);
    }
}

angular.module('app.retailes.common').component('selectClassificationCategoriesTree', {
    template: require('./select-classification-categories-tree.html'),
    controller: SelectClassificationCategoriesTreeController,
    controllerAs: 'ctrl',
    bindings: {
        resolve: '<',
        close: '&',
        dismiss: '&'
    }
});



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer.common/modules/categories/select-classification-categories-tree/select-classification-categories-tree.component.ts